import React from 'react'
import Select, { components } from 'react-select'
import { useIntl } from 'react-intl'

// Decorative, non-translatable glyphs (hoisted so they aren't raw JSX literals).
const CHECK_ICON = '✅'
const CROSS_ICON = '🚫'
const EMPTY_ICON = '☐'

const CustomValueContainer = ({children, ...props}: any) => {
  const intl = useIntl()
  const selected = props.getValue()
  const count = selected.length

  if (count <= 1) {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>
  }

  // Extraer solo el Input (necesario para que el usuario pueda escribir para filtrar)
  const input = React.Children.toArray(children).find(
    (child: any) => child?.type?.name === 'DummyInput' || child?.props?.id?.includes('input') || child?.key === 'input'
  ) || (Array.isArray(children) ? children[children.length - 1] : null)

  return (
    <components.ValueContainer {...props}>
      <span style={{color: '#3f4254', fontWeight: 500, paddingLeft: '4px'}}>
        {intl.formatMessage({id: 'common.multiSelect.countSelected', defaultMessage: '{count} selected'}, {count})}
      </span>
      {input}
    </components.ValueContainer>
  )
}

const CustomMenu = (props: any) => {
  const intl = useIntl()
  const { options, selectProps } = props
  const { onSelectAll, onClearAll } = selectProps

  const inputValue = selectProps.inputValue?.toLowerCase() || ''
  const visibleOptions = options.flatMap((opt: any) =>
    opt.options ? opt.options : [opt]
  ).filter((opt: any) => opt.label?.toLowerCase().includes(inputValue))

  return (
    <components.Menu {...props}>
      <div
        style={{
          padding: '6px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e9ecef',
        }}
      >
        <div
          style={{ cursor: 'pointer', color: '#007bff', fontSize: '13px' }}
          onClick={(e) => {
            e.stopPropagation()
            onSelectAll?.(visibleOptions)
          }}
        >
          {CHECK_ICON} {intl.formatMessage({id: 'common.action.selectAll', defaultMessage: 'Select All'})}
        </div>
        <div
          style={{ cursor: 'pointer', color: '#dc3545', fontSize: '13px' }}
          onClick={(e) => {
            e.stopPropagation()
            onClearAll?.()
          }}
        >
          {CROSS_ICON} {intl.formatMessage({id: 'common.action.deselectAll', defaultMessage: 'Deselect All'})}
        </div>
      </div>
      <components.MenuList {...props} />
    </components.Menu>
  )
}

const CustomOption = (props: any) => {
  return (
    <components.Option {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '10px' }}>
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => null}
          style={{ pointerEvents: 'none' }}
        />
        <span>{props.children ?? props.data?.label}</span>
      </div>
    </components.Option>
  )
};

const CustomGroupHeading = (props: any) => {
  const intl = useIntl()
  const { data, selectProps } = props
  const { value, onChange } = selectProps

  const handleCategoryClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    // Get all options in this group
    const groupOptions = data.options || []

    // Check if all options in this group are already selected
    const allSelected = groupOptions.every((opt: any) =>
      value?.some((v: any) => v.value === opt.value)
    )

    if (allSelected) {
      // Deselect all options in this group
      const newValue = value?.filter((v: any) =>
        !groupOptions.some((opt: any) => opt.value === v.value)
      ) || []
      onChange(newValue)
    } else {
      // Select all options in this group (add missing ones)
      const existingValues = value || []
      const newOptions = groupOptions.filter((opt: any) =>
        !existingValues.some((v: any) => v.value === opt.value)
      )
      onChange([...existingValues, ...newOptions])
    }
  }

  return (
    <components.GroupHeading {...props}>
      <div
        style={{
          fontWeight: 600,
          padding: '4px 12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          backgroundColor: '#f8f9fa'
        }}
        onClick={handleCategoryClick}
        title={intl.formatMessage({id: 'common.multiSelect.toggleCategoryHint', defaultMessage: 'Click to select/deselect all in this category'})}
      >
        <span style={{ fontSize: '11px' }}>
          {props.data.options?.every((opt: any) =>
            props.selectProps.value?.some((v: any) => v.value === opt.value)
          ) ? CHECK_ICON : EMPTY_ICON}
        </span>
        <span>{props.children}</span>
      </div>
    </components.GroupHeading>
  )
};

export const MultiSelectWithActions = (props: any) => {
  const { options, value, onChange } = props

  // Helper para aplanar opciones con grupos
  const flattenOptions = (opts: any[]) =>
    opts.flatMap((opt: any) => (opt.options ? opt.options : [opt]))

  const handleSelectAll = (opts: any[]) => {
    const flattened = flattenOptions(opts)
    const existingValues = Array.isArray(value) ? value : []
    const getOptionKey = (option: any) =>
      String(props.getOptionValue ? props.getOptionValue(option) : option?.value)
    const selectedKeys = new Set(existingValues.map(getOptionKey))
    const missingOptions = flattened.filter((option: any) => {
      const key = getOptionKey(option)
      if (selectedKeys.has(key)) return false
      selectedKeys.add(key)
      return true
    })

    onChange([...existingValues, ...missingOptions])
  }

  const handleClearAll = () => {
    onChange([])
  }

  return (
    <Select
      {...props}
      isMulti
      options={options}
      value={value}
      components={{ Menu: CustomMenu, Option: CustomOption, GroupHeading: CustomGroupHeading, ValueContainer: CustomValueContainer }}
      onSelectAll={handleSelectAll}
      onClearAll={handleClearAll}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
    />
  )
}
